import { visualizationSpec } from '@qronstat/ui/charts/registry';
import type { WidgetDef } from '../screens/types';
import type { ReportDefinition } from './types';

const compareGranularity = {
  hour: 'day',
  day: 'day',
  week: 'week',
  month: 'month',
  quarter: 'month',
  year: 'month',
} as const;

export function reportWidget(
  def: ReportDefinition,
  title: string,
  objectTypeOf: (member: string) => string | null = () => null,
): WidgetDef | null {
  const base = { id: 'report', title: 'reports.preview' as const, titleText: title, span: 12 as const };
  const metric = def.metrics[0];
  const compare = def.compare !== 'none' ? [def.compare] : [];
  const render = visualizationSpec(def.view).render;
  switch (render.widget) {
    case 'kpis':
      return def.metrics.length
        ? {
            ...base,
            kind: 'kpis',
            items: def.metrics.map((m) => ({ metric: m, compare, spark: true })),
          }
        : null;
    case 'series':
      if (!metric) return null;
      if (def.granularity)
        return {
          ...base,
          kind: 'timeseries',
          metrics: def.dimensions[0] ? [metric] : def.metrics,
          chart: render.chart,
          granularities: [def.granularity],
          defaultGranularity: def.granularity,
          compare: def.compare,
          ...(def.dimensions[0] ? { splitBy: def.dimensions[0] } : {}),
          ...(def.stacked ? { stacked: true } : {}),
        };
      if (def.dimensions[0])
        return {
          ...base,
          kind: 'distribution',
          dimension: def.dimensions[0],
          metric,
          chart: render.byDimension,
        };
      return { ...base, kind: 'kpis', items: def.metrics.map((m) => ({ metric: m, compare })) };
    case 'distribution':
      return metric && def.dimensions[0]
        ? { ...base, kind: 'distribution', dimension: def.dimensions[0], metric, chart: render.chart }
        : null;
    case 'heatmap':
      return metric ? { ...base, kind: 'heatmap', metric } : null;
    case 'funnel':
      return { ...base, kind: 'funnel', ...(def.events?.length ? { events: def.events } : {}) };
    case 'cohort':
      return { ...base, kind: 'cohort', ...(metric ? { metric } : {}) };
    case 'compare': {
      const objectType = def.dimensions[0] ? objectTypeOf(def.dimensions[0]) : null;
      return metric && objectType
        ? {
            ...base,
            kind: 'compare',
            objectType,
            metric,
            granularity: compareGranularity[def.granularity ?? 'day'],
          }
        : null;
    }
    case 'sinceStart': {
      const objectType = def.dimensions[0] ? objectTypeOf(def.dimensions[0]) : null;
      return metric && objectType
        ? {
            ...base,
            kind: 'sinceStart',
            objectType,
            metric,
            granularity: 'week',
            limit: Math.min(def.limit, 10),
          }
        : null;
    }
    case 'table':
      return def.metrics.length
        ? {
            ...base,
            kind: 'table',
            metrics: def.metrics,
            dimensions: def.dimensions,
            ...(def.granularity ? { granularity: def.granularity } : {}),
            compare: def.compare,
            limit: def.limit,
          }
        : null;
  }
}
